/**
 * Checks if a value is a plain JavaScript object (not an array, date, or other object type).
 * @param o - The value to check
 * @returns True if the value is a plain object created with {} or Object constructor
 */
export function isObject(o) {
    return o instanceof Object && o.constructor === Object;
}

/**
 * Extracts and transforms the _path property from an AEM content object.
 * Converts kebab-case to camelCase for the last path segment.
 * @param o - The object containing a _path property
 * @returns The transformed path or null if not found
 * @example
 * // Input: { _path: '/content/dam/my-content-item' }
 * // Returns: 'myContentItem'
 */
export function getPath(o) {
    if (!o) return null;

    let p = o['_path'];

    if (typeof p === 'string') {
        // Extract last segment and convert kebab-case to camelCase
        p = p
            .split('/')
            .pop()
            .replace(/-./g, (x) => x[1].toUpperCase());
    }

    return p;
}

/**
 * Deeply compares two objects and collects differences and missing properties.
 * Used for validating AEM content structure.
 * @param obj1 - The source object to compare
 * @param obj2 - The target object to compare against
 * @param stack - Internal parameter tracking the current path in the object tree
 * @param errors - Internal parameter collecting error messages
 * @returns Array of error messages describing differences
 */
export function deepCompare(obj1, obj2, stack: string[] = [], errors = []) {
    for (const k in obj1) {
        if (Object.prototype.hasOwnProperty.call(obj2, k) && obj2[k]) {
            if (typeof obj1[k] == 'object') {
                deepCompare(obj1[k], obj2[k], [...stack, k], errors);
            } else {
                if (obj1[k] != obj2[k]) {
                    const msg = 'different values at ' + obj1['_path'];
                    if (!errors.includes(msg)) {
                        errors.push(msg);
                    }
                }
            }
        } else {
            errors.push(
                'ERROR value "' +
                JSON.stringify(isObject(obj1[k]) ? transform(obj1[k], []) : obj1[k]) +
                '" does not exist in ' +
                obj1['_path'],
            );
        }
    }
    return errors;
}

/**
 * List of AEM content property keys that should always be preserved during transformations.
 * These keys contain actual content rather than structural metadata.
 */
export const importantKeys = [
    'heading',
    'labelText',
    'subheading',
    'html',
    'labelValue',
    'keyCode',
    'hintText',
    'plaintext',
];

/**
 * Transforms the input data object based on the specified path.
 *
 * @param data - The input object to be transformed. It is a record with keys of any type and values of any type.
 * @param path - An array of strings representing the path to traverse within the data object.
 * @param includePath - A boolean flag indicating whether to include the path in the transformed result. Defaults to false.
 * @returns A new object that is the result of the transformation.
 * 
 * @remarks
 * This function recursively processes AEM content objects:
 * 1. Arrays: Transforms each item and uses the _path property or generates text1, text2, etc. keys
 * 2. Objects: Recursively transforms nested objects using the _path property as the key
 * 3. Important keys: Always preserved in the output
 * 4. Returns the first value if only one property exists
 * 5. Returns null if no properties remain after transformation
 */
export function transform(
    data: Record<any, any>,
    path: string[],
    includePath = false,
): Record<any, any> {
    const newObj = {};

    if (includePath && data['_path']) {
        newObj['_path'] = data['_path'];
    }

    for (const k in data) {
        // Handle array properties - transform each item
        if (Array.isArray(data[k])) {
            for (let i = 0; i < data[k].length; i++) {
                const item = data[k][i];

                const p = getPath(item);

                const traverseResult = transform(item, path.concat(k + '[' + i + ']'), includePath);

                if (!traverseResult) continue;

                // Use _path as key if available, otherwise use text1, text2, etc.
                if (p) {
                    newObj[p] = traverseResult;
                } else {
                    newObj['text' + (i + 1)] = traverseResult;
                }
            }
        }

        // Handle nested object properties
        if (isObject(data[k])) {
            const p = getPath(data[k]);

            const traverseResult = transform(data[k], path.concat(k), includePath);

            if (!traverseResult) continue;

            // if it has path, use it as key; otherwise use original key
            if (p) {
                newObj[p] = traverseResult;
            } else {
                newObj[k] = traverseResult;
            }
        }

        // Preserve important keys (actual content properties)
        if (importantKeys.includes(k) && data[k]) {
            newObj[k] = data[k];
        }
    }

    // Simplify single-property objects by returning just the value
    if (Object.keys(newObj).length === 1) {
        return Object.values(newObj)[0];
    }

    // Return null for empty objects
    if (Object.keys(newObj).length === 0) {
        return null;
    }

    return newObj;
}

/**
 * Performs a deep update on a target object by applying the provided updates.
 * This function recursively traverses the target object and updates its properties
 * based on the keys and values provided in the `updates` object.
 *
 * @param obj1 - The target object to be updated (original content). This object will be modified directly.
 * @param obj2 - The updates object containing the updates to apply. The structure of this
 *                  object should match the structure of the target object for the
 *                  properties that need to be updated.
 * @returns The updated target object with changes from obj2 merged in.
 *
 * @example
 * const target = { a: { b: 1, c: 2 }, d: 3 };
 * const updates = { a: { b: 10 } };
 * const result = deepUpdate(target, updates);
 * console.log(result); // { a: { b: 10, c: 2 }, d: 3 }
 * 
 * @remarks
 * This function is used to merge local content modifications with AEM GraphQL content:
 * - Handles arrays by recursively updating each element at the same index
 * - Handles nested objects by recursively merging properties
 * - Overwrites primitive values when types differ
 * - Skips properties that don't exist in the updates object
 */
export function deepUpdate(obj1, obj2) {
    if (obj1 === undefined) return obj2;
    if (obj2 === undefined) return obj1;
    if (!Array.isArray(obj1) && !isObject(obj1) && obj1 !== obj2) return obj2;

    const newObj = { ...obj1 };

    for (const k in newObj) {
        if (!Object.prototype.hasOwnProperty.call(obj2, k) || !obj2[k]) {
            continue;
        }

        // Recursively update array elements at matching indices
        if (Array.isArray(newObj[k]) && Array.isArray(obj2[k])) {
            newObj[k] = newObj[k].map((item, index) => deepUpdate(item, obj2[k][index]));
        } else if (isObject(newObj[k])) {
            // Recursively merge nested objects
            newObj[k] = deepUpdate(newObj[k], obj2[k]);
        } else if (typeof newObj[k] != obj2[k]) {
            // Overwrite if types differ
            newObj[k] = obj2[k];
        }
    }

    return newObj;
}
